/**
 * /guides/how-to-collect-wedding-photos-from-guests
 *
 * "How to Collect Wedding Photos From Guests" — the sixth production guide, and
 * the first outside the seating and RSVP clusters. It opens the photo cluster,
 * which the seventh guide continues with the QR code question specifically.
 *
 * Its argument is that guests already take the photographs; what fails is the
 * return path. So the article is organised around one chain and keeps it
 * explicit throughout: a guest takes a photo, shares or uploads it, the couple
 * collects it, the collection stays organized, and it becomes part of the
 * wedding archive. Do not collapse a link out of that chain in a later edit —
 * it is what makes the piece answer the question rather than list tools.
 *
 * ── Deliberately NOT a QR article ─────────────────────────────────────────
 * The next guide is "Wedding QR Code for Photos: How Does It Work?", so QR
 * appears here only as one convenient way to reach an upload page. Nothing in
 * this file may drift into QR generation, QR design, print dimensions,
 * placement optimization, scanning instructions or troubleshooting. If a future
 * edit wants to explain how a QR code works, it belongs in the other guide.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * No method is described as universally best, and none is dismissed. The
 * comparison exists to expose a trade-off (guest effort against organization
 * against what happens to the media), not to walk the reader to a conclusion.
 * Three hedges in particular must survive editing: messaging platforms "may"
 * compress or process media depending on how it is shared, because behaviour
 * differs by platform and by whether the sender chose a document or a photo;
 * shared-album services differ in what they need from a guest, because Google
 * Photos and Apple Shared Albums are examples rather than the subject; and
 * asking after the wedding is described as extra friction, never as something
 * that fails. A reader whose family group chat worked perfectly well is not
 * doing it wrong.
 *
 * Every quantified participation claim is banned outright: no "10x more
 * photos", no share of guests who upload, no number of photos a wedding
 * produces. None of it is measured, and the article does not need it.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Six, all in the closing section, and nothing beyond them:
 *
 *   - "the guest gallery is part of every Agapaé plan, with different limits
 *     on each"  (the note)
 *     True at `api/constants/planLimits.mjs`: all three tiers carry a gallery
 *     with limits. NOT "photo and video gallery" — Blossom sold from 2026-10-14
 *     is photos only (`videoUploads: false`).
 *   - guests upload photos and videos to the wedding gallery  (the upload flow;
 *     videos are plan-dependent, which the limits sentence says)
 *   - guests reach it through the event's guest-facing upload flow
 *   - a QR code is one of the ways they reach it  (PLANS comment: Blossom and
 *     Bouquet share via a `/j/:eventId/c/:code` link or QR)
 *   - the gallery is private to the event rather than a public feed
 *   - the plan sets the limits: how many guests can upload (`maxGuests`), how
 *     much the gallery holds (`maxPhotos` / `maxBytes`), how long the upload
 *     window runs before and after the wedding (`preEventWindowDays` /
 *     `accessDays`), and whether guests can add videos (`videoUploads`)
 *
 * And the things this guide must never say, because this article is not the
 * place to assert them: no account required, no app required, original quality
 * on every plan, permanent storage, automatic backup, automatic sharing,
 * automatic moderation, a live slideshow, live upload notifications, guest
 * identity verification, any link between a guest's identity and their photos,
 * automatic organization of uploaded media, AI selection of photos, guaranteed
 * uploads, or automatic reminders. Several of those are simply not built; the
 * rest are product-page copy that a guide has no business restating as fact.
 *
 * "Unlimited" appears nowhere in this file. The word carries a fair-use
 * qualification everywhere it is used legitimately, and an unqualified one here
 * would be the exact claim the content principles rule out.
 *
 * ── Three structural notes ────────────────────────────────────────────────
 * The four methods keep their "Option N:" prefix, unlike the ordinals the third
 * and fifth guides dropped. They are parallel alternatives rather than a
 * sequence, and the number is what tells a reader scanning the page that the
 * fourth heading is still part of the same set.
 *
 * The source outline suggests a comparison table. `BLOCK_TYPES` has no table,
 * and teaching both renderers about one is infrastructure work this content
 * change has no reason to do — so the comparison is a `subheading` per method
 * with its trade-offs beneath it, which is the same call the fourth guide made
 * for its timeline and reads better on a phone than a four-column table would.
 *
 * One product link, in the closing section, plus the same href under the
 * related block. `related.guides` names the QR guide this one hands off to,
 * which links back here for the destination decision it does not cover; a link
 * to an unregistered slug fails the test rather than shipping a 404.
 *
 * ── One thing to leave alone ──────────────────────────────────────────────
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const howToCollectWeddingPhotosFromGuestsEn = {
  slug: "how-to-collect-wedding-photos-from-guests",

  title: "How to Collect Wedding Photos From Guests",
  metaTitle: "How to Collect Wedding Photos From Guests | Agapaé",
  metaDescription:
    "Learn how to collect wedding photos from guests, compare shared albums, group chats and QR uploads, and keep everyone's memories in one place.",

  eyebrow: "Wedding photos",

  hero: {
    src: "/assets/images/invitation/invitation-2.jpg",
    // Describes the photograph, not the keyword: a black and white outdoor
    // ceremony photographed from the back of the aisle, rows of seated guests
    // filling both sides of the frame, the couple facing each other with the
    // officiant between two tall floral arrangements, and a wooden fence and
    // faint mountains behind. It is the guests' own vantage point, which is
    // why it belongs to this article — and it is genuinely what is in the
    // frame. Describing a phone or a camera that is not visible would be a lie
    // told to exactly the readers who cannot see it.
    alt: "A black and white photograph of an outdoor wedding ceremony seen from among the guests, rows of seated people filling both sides of the frame and the couple standing with their officiant between two tall floral arrangements",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Your photographer captures the story you planned: the ceremony, the portraits, the first dance. Your guests capture the rest of it, from wherever they happen to be standing.`,
    `Most of those photographs never reach the couple. They stay in camera rolls, a handful appear in a group chat, and the rest are forgotten.`,
    `The difficulty is almost never getting guests to take photos. It is getting the wedding guest photos back to you in a form you can keep.`,
    `This guide is about closing that gap: one simple path from your guests' camera rolls to one place that belongs to you.`,
  ],

  sections: [
    {
      heading: "Why Collect Photos From Wedding Guests?",
      content: [
        `Guest photos are not a cheaper version of professional photography. They are a different point of view, taken from where your photographer was not.`,
        `What they tend to hold:`,
        {
          type: "list",
          items: [
            `what was happening at another table`,
            `reactions during the speeches, rather than the speaker`,
            `friends on the dance floor, late and unposed`,
            `getting ready, and the quiet hours before the ceremony`,
            `informal portraits taken between the planned ones`,
            `moments before the photographer arrived, or after they left`,
          ],
        },
        `None of it competes with your professional gallery. It sits alongside it, and it is the part guests are most eager to send you.`,
      ],
    },
    {
      heading: "The Best Way to Collect Wedding Photos From Guests",
      content: [
        `No single method suits every wedding, but the setups that work share three characteristics.`,
        {
          type: "list",
          items: [
            `One obvious place to upload, so nobody has to ask where the photos go.`,
            `Very little friction for the guest, because the request competes with a celebration.`,
            `A collection you can actually reach and keep afterwards.`,
          ],
        },
        `Every method below is an attempt at the same short chain: a guest takes a photo, shares or uploads it, you collect it, the collection stays organized, and it becomes part of your wedding archive.`,
        `Most break at the second link. A guest asked to remember a process days later has already moved on; a guest asked to share the photo they are looking at has almost nothing to do.`,
      ],
    },
    {
      heading: "Option 1: A Shared Online Album",
      content: [
        `The model is familiar: you create a shared album, give your guests access, and everything they add collects in one place you can browse. Google Photos and Apple Shared Albums are the best-known examples.`,
        { type: "subheading", text: "Advantages" },
        {
          type: "list",
          items: [
            `many guests have used something like it before`,
            `the photos collect in one album rather than in separate messages`,
            `guests can often see what everyone else has added`,
            `nothing extra to pay for, if you already live in that ecosystem`,
          ],
        },
        { type: "subheading", text: "Limitations" },
        {
          type: "list",
          items: [
            `depending on the service, a guest may need a particular account or an invitation first`,
            `what a guest can do varies between platforms and between phones`,
            `sharing settings decide who can view and contribute, so check them early`,
            `how the media is stored, and at what quality, is worth confirming`,
          ],
        },
      ],
    },
    {
      heading: "Option 2: Ask Guests to Send Photos in a Group Chat",
      content: [
        `Most weddings fall into this one without deciding to: guests send photos through WhatsApp, Messenger, or whichever chat already exists.`,
        { type: "subheading", text: "Why it works" },
        {
          type: "list",
          items: [
            `there is nothing to learn and nothing to explain`,
            `guests already know how to send a photo this way`,
            `no new destination has to be introduced during the celebration`,
          ],
        },
        { type: "subheading", text: "Where it starts to break down" },
        {
          type: "list",
          items: [
            `photos arrive mixed into conversation, jokes and logistics`,
            `several chats usually exist, so the collection is split before it starts`,
            `finding one photo months later means scrolling a thread`,
            `messaging platforms may compress or process media, depending on the platform and how it is shared`,
            `videos are the most awkward part, and the most likely to be refused`,
          ],
        },
        `A group chat is a conversation that happens to contain images; organization, more than quality, is what costs you photographs.`,
      ],
    },
    {
      heading: "Option 3: Ask Guests to Send Photos After the Wedding",
      content: [
        `"Send us your photos when you get home" does work, particularly with a small group of close friends and family.`,
        `By then, the photos are buried under everything taken since:`,
        {
          type: "list",
          items: [
            `guests have already moved on to the following week`,
            `good intentions fade, and nobody feels chased`,
            `the photos sit in a camera roll nobody scrolls back through`,
            `what does arrive comes by email, message and link, in no order`,
          ],
        },
        `Asking afterwards does not fail. It adds an action after the event, and every extra action costs you part of the collection.`,
      ],
    },
    {
      heading: "Option 4: Use a Wedding Photo Upload Page",
      content: [
        `The fourth approach is a destination built for this: a page where guests upload the photos and videos they took, into one collection that belongs to you.`,
        `From the guest's side, the whole experience is three moves:`,
        {
          type: "steps",
          items: [
            `Open the upload page.`,
            `Choose the photos and videos to share.`,
            `Upload them.`,
          ],
        },
        `A link works; a QR code works better in a room because it removes the typing. It is a way of reaching the page, not a different method.`,
        `What separates it from the other three is that guest photo upload is the only thing happening there.`,
      ],
    },
    {
      heading: "Make It Easy to Find the Upload Method",
      content: [
        `Even a good collection method underperforms if guests never notice it, and most people will not go looking.`,
        `Places couples commonly use:`,
        {
          type: "list",
          items: [
            `table cards`,
            `welcome signage at the entrance`,
            `the guest book area`,
            `the bar`,
            `wherever photographs are already being taken`,
            `your wedding website`,
            `the invitation information, where it fits naturally`,
          ],
        },
        `It does not need to be everywhere. Put the instruction where a guest already has a reason to look, when their phone is likely in their hand.`,
      ],
    },
    {
      heading: "Ask for Photos During the Wedding, Not Only After",
      content: [
        `Compare two requests.`,
        `"Send us your photos after the wedding" asks a guest to remember something, find it again later, and do the work in their own time.`,
        `"If you take a photo you love, add it here" asks them to act while the phone is in their hand and the photo is on the screen.`,
        `A brief mention during the celebration does more than a card alone. Keep asking afterwards too: a collection that stays open catches the guest who was busy dancing.`,
      ],
    },
    {
      heading: "What About Videos?",
      content: [
        `Guests film more than they used to, and the clips are often the parts nobody else has:`,
        {
          type: "list",
          items: [
            `speeches and toasts, in full`,
            `the dance floor, from inside it`,
            `short candid moments between the planned ones`,
            `behind the scenes while everyone is getting ready`,
          ],
        },
        `Video is where collection methods differ most: services vary in the formats they accept, the size and length they allow, and what happens to the quality once uploaded.`,
        `If video matters to you, test it with a real clip from a phone rather than assuming photo support means video support.`,
      ],
    },
    {
      heading: "Think About Privacy and Access",
      content: [
        `Guest media is personal, and it includes people who did not choose the tool you did. Before sharing a link widely, know:`,
        {
          type: "list",
          items: [
            `who can view the collection`,
            `who can add to it`,
            `whether a public link exposes it to anyone who finds it`,
            `how long it stays available`,
            `how you can download or export the files afterwards`,
          ],
        },
        `A link anyone can open is convenient and public; a collection limited to your wedding is more private and takes a step to reach. Decide which you want on purpose.`,
      ],
    },
    {
      heading: "One Collection Is Better Than Five",
      content: [
        `Weddings drift into a familiar pattern: one chat for one partner's friends, another for the other's, a family group, a shared album, and a folder link from a cousin.`,
        `Each collects real photographs. Together they leave you five places to check and five things to download, which is a collection problem rather than a wedding photo album.`,
        `Choose one main destination and let everything else feed into it. If a photo arrives another way, add it there yourself rather than starting a sixth place.`,
        `The goal is answering one question without hesitating: where are our wedding photos?`,
      ],
    },
    {
      heading: "A Simple Wedding Guest Photo Collection Plan",
      content: [
        `Put together, the whole thing is short enough to write down:`,
        {
          type: "steps",
          items: [
            `Choose one main place where guest photos will be collected.`,
            `Make sure guests can reach it easily from their phones.`,
            `Test the upload experience yourself before the wedding.`,
            `Put the instruction somewhere your guests will see it.`,
            `Mention it briefly during the celebration.`,
            `Let guests upload photos and videos as they take them.`,
            `Keep the collection open afterwards for the ones that arrive late.`,
            `Download or preserve the media according to the terms of whatever you chose.`,
          ],
        },
        `The third step is the one couples skip; ten minutes with your own phone is what makes the rest work.`,
      ],
    },
    {
      heading: "Shared Album, Group Chat or Dedicated Upload Page?",
      content: [
        `The comparison is a set of trade-offs rather than a winner.`,
        { type: "subheading", text: "A shared album" },
        {
          type: "list",
          items: [
            `Guest effort: low to medium, depending on the service.`,
            `Organization: good, since everything lands in one album.`,
            `Media: depends on the service; check what it does with video.`,
            `Best for: couples already in a photo ecosystem.`,
          ],
        },
        { type: "subheading", text: "A group chat" },
        {
          type: "list",
          items: [
            `Guest effort: very low, with nothing new to learn.`,
            `Organization: poor to medium; it degrades as the thread grows.`,
            `Media: may be compressed or processed, depending on the platform.`,
            `Best for: small groups and casual sharing.`,
          ],
        },
        { type: "subheading", text: "Sending photos after the wedding" },
        {
          type: "list",
          items: [
            `Guest effort: medium, because it is a separate task.`,
            `Organization: poor, since everything arrives differently.`,
            `Media: depends on how each guest sends it.`,
            `Best for: a few close friends and family.`,
          ],
        },
        { type: "subheading", text: "A dedicated upload page" },
        {
          type: "list",
          items: [
            `Guest effort: low, and it happens in the moment.`,
            `Organization: strong; collecting is all the page does.`,
            `Media: depends on the service, so check the video limits.`,
            `Best for: couples who want one central guest-photo collection.`,
          ],
        },
        `A dedicated page is not automatically the right answer. It is the one that most reliably produces a single organized collection.`,
      ],
    },
    {
      heading: "What to Look For in a Wedding Photo Collection Tool",
      content: [
        `Whatever you are comparing, a short checklist tells you most of what you need:`,
        {
          type: "list",
          items: [
            `a simple experience for a first-time guest`,
            `works on the phones your guests actually carry`,
            `one clear upload destination`,
            `photo support that suits how your guests shoot`,
            `video support, if video matters to you`,
            `privacy and access controls you can understand`,
            `a storage period long enough to be useful`,
            `a way to retrieve or export the files afterwards`,
            `one collection, not five scattered channels`,
          ],
        },
        `Test anything that leaves one of those open before the wedding, not after.`,
      ],
    },
    {
      // The source outline closes with a separate Agapaé section, which is the
      // heading the `related` block below already renders as an H2. Two H2s
      // reading the same words is a defect, and all five earlier guides
      // resolved it the same way: the closing prose section is named for what
      // it does, and "Where this fits in Agapaé" stays with the related block.
      heading: "From Guest Photos to Your Wedding Archive",
      content: [
        `Collecting the photos is the first half of the job. The second half is a collection you can sit down with: media to review, download, share with the family who ask, and keep alongside your professional gallery.`,
        `That is the whole chain: a guest takes a photo, uploads it, you collect it, it stays organized, and it becomes part of your wedding archive.`,
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type the five earlier guides put theirs.
          type: "note",
          text: `The guest gallery is part of every Agapaé plan, with different limits on each.`,
        },
        `In Agapaé, guests reach the gallery through the event's guest upload flow, opened from a QR code or a link, and add the photos and videos they took. The gallery is private to the event rather than a public feed.`,
        `The plan sets the limits: how many guests can upload, how much the gallery holds, how long the upload window stays open before and after the wedding, and whether guests can add videos as well as photos.`,
        {
          type: "p",
          text: [
            `For the product-specific experience, see `,
            { text: "wedding photo sharing", href: "/wedding-photo-sharing" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the five earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What is the easiest way to collect wedding photos from guests?`,
      a: `The one that asks least of your guests: a single destination they can open on their phone during the celebration, with no explanation needed. That may be a shared album, or an upload page reached from a link or a QR code. What matters more than the tool is that there is only one of them.`,
    },
    {
      q: `How can wedding guests share photos without sending them one by one?`,
      a: `Give them somewhere to add a whole selection at once. Shared albums and upload pages both let a guest pick several photos from their camera roll and send them together. In a message thread the same photos arrive piecemeal, and have to be pulled back out of the conversation later.`,
    },
    {
      q: `Can wedding guests upload videos as well as photos?`,
      a: `Often, though not always in the same way, so check before you commit. Services differ in the formats they accept, the file size and length they allow, and what happens to the quality afterwards. Since speeches and dance-floor clips are usually the moments only guests have, test it with a real video from a phone.`,
    },
    {
      q: `Should I use a shared album or a group chat for wedding photos?`,
      a: `A shared album keeps the photos together and out of the conversation, which makes them easier to browse and download later. A group chat is more familiar and needs no setup, but the images end up mixed into the thread and may be compressed depending on the platform. If your guests already use an album, that is the tidier of the two.`,
    },
    {
      q: `When should I ask guests to share their wedding photos?`,
      a: `During the wedding, and again afterwards. Asking in the moment reaches guests while the photo is still on screen and costs them one action. Keeping the collection open after the day catches the guests who were too busy celebrating, and anyone who wants to go through their camera roll properly first.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The QR code guide this article hands off to conceptually, now that it is
    // written and registered. The pair is reciprocal: that guide links back
    // here for the destination decision, which is the half it deliberately does
    // not cover. A slug that is not registered fails the test rather than
    // shipping a 404, so this list stays in step with the registry.
    guides: ["wedding-qr-code-for-photos"],

    product: [
      {
        href: "/wedding-photo-sharing",
        label: "Wedding photo sharing",
        blurb:
          "See how guests upload photos and videos to a wedding gallery that is private to the event, reached from a QR code or a link. The gallery is part of every Agapaé plan, with different limits on each.",
      },
    ],
  },
};

export default howToCollectWeddingPhotosFromGuestsEn;
